// PasteLinkBar — compact, single-line command bar to decide on a specific market.
//
// Behavior:
//   - Validates pasted Polymarket URL via parseMarketInput (zero engine logic here).
//   - On submit, navigates to /?q={url}&autorun=1 (cleanly encoded).
//   - Inline calm error state for invalid/unsupported URLs.
//   - Mobile-first: input shrinks, button never overflows.
//
// Variants:
//   - "command"   → signed-in cockpit, prominent border
//   - "secondary" → signed-out hero, muted, sits below primary CTA
//   - "compact"   → empty-state helper inside Feed/Watchlist
//
// Honesty: no fake URL completion, no autocomplete, no fake market preview.

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Link2, ArrowRight, AlertCircle } from "lucide-react";
import { parseMarketInput } from "@/lib/marketParser";

type Variant = "command" | "secondary" | "compact";

interface Props {
  variant?: Variant;
  /** Optional eyebrow override; default depends on variant. */
  label?: string;
}

const PLACEHOLDER = "Paste a Polymarket URL";

export default function PasteLinkBar({ variant = "command", label }: Props) {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [touched, setTouched] = useState(false);

  const parsed = useMemo(() => parseMarketInput(value), [value]);
  const trimmed = value.trim();

  // Only flag as invalid once the user has typed something AND interacted.
  const showError =
    touched &&
    trimmed.length > 0 &&
    (parsed.reason === "unsupported_domain" ||
      parsed.reason === "no_market_path" ||
      parsed.reason === "invalid_url" ||
      parsed.reason === "kalshi_unavailable");

  const errorMsg =
    parsed.reason === "kalshi_unavailable"
      ? "Kalshi is paused while we rebuild signal coverage."
      : parsed.reason === "unsupported_domain"
      ? "Only Polymarket URLs are supported right now."
      : parsed.reason === "no_market_path"
      ? "That looks like the homepage — paste a specific market URL."
      : parsed.reason === "invalid_url"
      ? "That URL didn't parse. Check the link and try again."
      : null;

  const canSubmit =
    trimmed.length > 0 &&
    parsed.isValidMarket &&
    parsed.platform === "polymarket";

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    // Build a clean URL: properly encoded, no leftover params.
    const qs = new URLSearchParams({ q: parsed.raw, autorun: "1" });
    navigate(`/?${qs.toString()}`);
  };

  // Visual variants — tokens only, no raw colors.
  const wrapper =
    variant === "command"
      ? "rounded-xl border border-primary/25 bg-card shadow-sm"
      : variant === "secondary"
      ? "rounded-xl border border-border/60 bg-card/60"
      : "rounded-lg border border-border/50 bg-card/40";

  const eyebrow =
    label ??
    (variant === "command"
      ? "Decide on a specific market"
      : variant === "secondary"
      ? "Already have a market in mind?"
      : "Paste a market URL");

  return (
    <div className={`${wrapper} px-2.5 py-2 sm:px-3 sm:py-2.5 mb-3`}>
      <p className="text-[10px] font-body uppercase tracking-wider text-muted-foreground mb-1.5">
        {eyebrow}
      </p>
      <form onSubmit={onSubmit} className="flex items-center gap-2 min-w-0">
        <Link2 className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
        <input
          type="url"
          inputMode="url"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={PLACEHOLDER}
          aria-label="Paste a Polymarket market URL"
          aria-invalid={showError || undefined}
          className="flex-1 min-w-0 bg-transparent text-sm font-body text-foreground placeholder:text-muted-foreground/60 focus:outline-none py-1"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-body font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity btn-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card"
          aria-label="Decide on this market"
        >
          Decide
          <ArrowRight className="w-3.5 h-3.5" aria-hidden />
        </button>
      </form>
      {showError && errorMsg && (
        <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-body text-destructive leading-snug">
          <AlertCircle className="w-3 h-3 shrink-0" aria-hidden />
          {errorMsg}
        </p>
      )}
    </div>
  );
}
